import { ApolloClient } from "@apollo/client";
import { isEqual } from "lodash";
import { AgGrid } from "@jsq/ds/core";
import { DataVaultEntitiesDocument } from "~/graphql/supergraphHooks";
import { type DataVaultEntitiesQuery } from "~/graphql/supergraphTypes";
import { DataVaultEntitiesGQLFactory } from "./dataVaultEntitiesGQLFactory";

/**
 * Provides the data source for the Investment Entities grid using DataVaultEntities query.
 * Implements AG Grid's server-side data source interface for pagination and filtering.
 *
 * Uses offset-based pagination (converted to cursor-based by the backend) to support
 * direct page jumping in AG Grid.
 */
export class DataVaultEntitiesGQLDataSource implements AgGrid.IServerSideDatasource {
  private apiClient: ApolloClient<object>;
  private refInputFilter: Record<string, any> = {};
  private pageSize: number = 20;
  private extensionFields: any[] = [];
  private pendingRequests = new Map<number, boolean>();

  constructor(
    client: ApolloClient<object>,
    pageSize: number = 20,
    extensionFields: any[] = [],
  ) {
    this.apiClient = client;
    this.pageSize = pageSize;
    this.extensionFields = extensionFields;
  }

  /**
   * Fetch rows from the GraphQL API based on grid parameters
   * @param params - AG Grid server-side get rows parameters
   */
  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const { api, request } = params;
    const { filterModel, sortModel, startRow } = request;

    const rowKey = startRow ?? 0;

    if (this.pendingRequests.get(rowKey)) {
      return;
    }

    this.pendingRequests.set(rowKey, true);

    api.showLoadingOverlay();

    const inputFilter = DataVaultEntitiesGQLFactory.toInputFilterVariables(
      filterModel,
      this.extensionFields,
    );

    const filtersChanged = !isEqual(this.refInputFilter, inputFilter);
    if (filtersChanged) {
      this.refInputFilter = inputFilter;
    }

    const paginationArgs = DataVaultEntitiesGQLFactory.toPaginationVariables(
      rowKey,
      this.pageSize,
    );

    const sortVariables = DataVaultEntitiesGQLFactory.toSortVariables(sortModel);

    this.apiClient
      .query<DataVaultEntitiesQuery>({
        query: DataVaultEntitiesDocument,

        variables: {
          filters: {
            ...inputFilter,
            ...sortVariables,
          },
          first: paginationArgs.first,
          after: paginationArgs.after,
        },
        fetchPolicy: "no-cache",
      })
      .then((response) => {
        const successParams = DataVaultEntitiesGQLFactory.toLoadSuccessParams(
          response.data,
          rowKey,
        );

        if (successParams.resp.rowCount === 0) {
          api.showNoRowsOverlay();
        } else {
          api.hideOverlay();
        }
        params.success(successParams.resp);
      })
      .catch(() => {
        api.showNoRowsOverlay();
        params.success({ rowData: [], rowCount: 0 });
      })
      .finally(() => {
        this.pendingRequests.delete(rowKey);
      });
  }

  public destroy(): void {
    this.pendingRequests.clear();
  }
}
